<template>
  <fieldset>
    <div class="tla-form-group" id="PersonalDetails">
      <label class="tla-form-group__label tla-form-group__label--required" for="firstName"
        v-html="firstNameLabel"></label>

      <input id="firstName" v-validate.initial="'required'" data-vv-name="firstName" :data-vv-scope="validationScope"
        class="tla-form-group__input" :class="getValidationClass('firstName', 'input')" v-model="firstName"
        name="firstName" spellcheck="false" @click="logEvent('firstName')" :placeholder="firstNamePlaceholder">
      <small class="text-microcopy"><span>{{firstNameMicrocopy}}</span></small>
      <span class="tla-form-group__message tla-form-group__message--spaced"
        :class="getValidationClass('firstName', 'message')">{{ errors.first(`${validationScope}.firstName`) }}</span>
    </div>
    <div class="tla-form-group">
      <label class="tla-form-group__label tla-form-group__label--required" for="lastName"
        v-html="lastNameLabel"></label>

      <input id="lastName" v-validate.initial="'required'" data-vv-name="lastName" :data-vv-scope="validationScope"
        autocomplete="family-name" type="text" class="tla-form-group__input"
        :class="getValidationClass('lastName', 'input')" v-model="lastName" name="lastName" spellcheck="false"
        @click="logEvent('lastName')" :placeholder="lastNamePlaceholder">
      <span class="tla-form-group__message tla-form-group__message--spaced"
        :class="getValidationClass('lastName', 'message')">{{ errors.first(`${validationScope}.lastName`) }}</span>
    </div>
    <div class="tla-form-group">
      <label class="tla-form-group__label tla-form-group__label--required" for="email" v-html="emailLabel"></label>
      <div class="tla-form-group__wrapper">
        <input id="email" autocomplete="email" type="email" v-validate.initial="'required'" data-vv-name="email"
          :data-vv-scope="validationScope" class="tla-form-group__input"
          :class="{ 'tla-form-group__input--error': emailAttempted && errors.first(`${validationScope}.enhanced-email`) }"
          v-model="email" name="email" spellcheck="false" @click="logEvent('email')" :placeholder="emailPlaceholder">
        <span v-if="emailLoading" class="tla-form-group__wrapper--loading"></span>
      </div>
      <small class="text-microcopy"><span>{{ emailMicroCopyGetter }}</span></small>
      <span class="tla-form-group__message  tla-form-group__message--error tla-form-group__message--spaced"
        v-if="emailAttempted && errors.first(`${validationScope}.enhanced-email`)">{{
          errors.first(`${validationScope}.enhanced-email`) }}</span>
    </div>
  </fieldset>
</template>

<script lang="ts">
import VeeValidate, {
  FieldError,
  Validator
} from 'vee-validate';
import {
  Component,
  Prop,
  Vue,
  Watch,
  Inject
} from 'vue-property-decorator';
import {
  State,
  Getter,
  Action,
  Mutation,
  namespace
} from 'vuex-class';
import Debounce from 'debounce-decorator';
import { policyService } from '@/services/PolicyService';
import { loggingService } from '../../services/LoggingService';
import { validationService } from '../../services/ValidationService';
import { validationApiService } from '../../services/ValidationApiService';
import PersonalDetailsValidationMessages from '../../validation/personal-details';

const PolicyModule = namespace('policyModule');
const PersonalDetailsModule = namespace('personalDetailsModule');

Vue.use(VeeValidate, PersonalDetailsValidationMessages);

@Component
export default class KMIPersonalDetailsComponent extends Vue {
  @PolicyModule.Action public submitDefaultEmailMicroCopyText;

  @PersonalDetailsModule.Action public submitFirstName;

  @PersonalDetailsModule.Action public submitLastName;

  @PersonalDetailsModule.Action public submitEmail;

  @PersonalDetailsModule.Getter public personalDetails;

  @Inject('$validator') public $validator!: Validator;

  @Prop({ default: 'We\'ll send you a confirmation email' })
  public emailMicroCopy!: string;

  @Prop({ default: 'First name <span>*</span>' })
  public firstNameLabel!: string;

  @Prop({ default: 'Real names help us personalise your pricing information accurately' })
  public firstNameMicrocopy!: string;

  @Prop({ default: 'Last name <span>*</span>' })
  public lastNameLabel!: string;

  @Prop({ default: 'Email <span>*</span>' })
  public emailLabel!: string;

  @Prop({ default: 'First name here' })
  public firstNamePlaceholder!: string;

  @Prop({ default: 'Last name here' })
  public lastNamePlaceholder!: string;

  @Prop({ default: 'Provide email here' })
  public emailPlaceholder!: string;

  @Prop({ default: null })
  public validationScope!: string;

  @Watch('personalDetailsErrors')
  public logEvent(inputFieldName: string) {
    loggingService
      .recordEvent(
        'eventTracking',
        'formEvent',
        'Form Step Personal Details',
        inputFieldName
      );
  }

  private emailPrePopulated: boolean = false;

  private emailAttempted: boolean = false;

  private emailLoading: boolean = false;

  get firstName() {
    return this.personalDetails.firstName;
  }

  set firstName(fName: string) {
    this.submitFirstName(fName);
  }

  get lastName() {
    return this.personalDetails.surname;
  }

  set lastName(lName: string) {
    this.submitLastName(lName);
  }

  get email() {
    return this.personalDetails.email;
  }

  set email(email: string) {
    this.submitEmail(email);
  }

  get personalDetailsErrors(): FieldError[] {
    return this.$validator.errors.items;
  }

  public get emailMicroCopyGetter() {
    return policyService.emailMicroCopy;
  }

  public get isNextClicked() {
    return validationService.hasNextBeenClicked(this.validationScope);
  }

  @Watch('isNextClicked')
  public watchIsNext() {
    this.watchEmail();
    this.emailAttempted = true;
  }

  public getValidationClass(fieldName: string, fieldType: string) {
    return validationService
      .getValidationClass(this.$validator, fieldName, fieldType, false, false, this.validationScope);
  }

  @Watch('email')
  public async watchEmail() {
    this.$validator.errors.add({
      field: 'enhanced-email',
      msg: 'Please enter a valid email',
      scope: this.validationScope,
      id: 'enhanced-email'
    });

    this.validateEmail();
  }

  @Debounce(500)
  public async validateEmail() {
    this.emailLoading = true;

    const result = await validationApiService.isEmailValid(this.email, !this.emailPrePopulated);

    if (result) {
      this.emailLoading = false;
      this.$validator.errors.removeById('enhanced-email');
    } else {
      this.emailLoading = false;
    }

    this.emailAttempted = true;
    this.validateComponent();
  }

  public async validateComponent() {
    await this.$validator.validateAll(this.validationScope);
  }

  public async prePopulateFromQueryString() {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const firstNameParam = urlParams.get('firstName');

      if (firstNameParam && firstNameParam.length > 0) {
        this.firstName = firstNameParam;
      }

      const lastNameParam = urlParams.get('lastName');

      if (lastNameParam && lastNameParam.length > 0) {
        this.lastName = lastNameParam;
      }

      const emailParam = urlParams.get('email');

      if (emailParam && emailParam.length > 0) {
        this.email = emailParam;
        this.emailPrePopulated = true;
      }
    } catch (error) {
      console.log('URLSearchParams not supported');
    }
  }

  public created() {
    this.$validator.localize('en', PersonalDetailsValidationMessages.english);

    this.prePopulateFromQueryString();
    this.submitDefaultEmailMicroCopyText(this.emailMicroCopy);
  }
}
</script>
